import { Anchor, Button, Group, Stack, Text, Tooltip } from '@mantine/core';
import { Dropzone, FileRejection, FileWithPath } from '@mantine/dropzone';
import { ChangeEventHandler, useEffect, useRef } from 'react';
import { notifications } from '@mantine/notifications';
import {
  HiOutlineArrowUpTray,
  HiOutlineDocumentText,
  HiOutlineExclamationTriangle,
} from 'react-icons/hi2';
import type { RelativeFile } from '@/types/common';
import { formatBytes } from '@/utils/misc.ts';

type Props = {
  /** 'multiple' → folder support + appends to existing files
   *  'single'   → one file only, disallows .zip */
  mode: 'multiple' | 'single';
  /** For 'multiple', pass current files so we can append. Optional for 'single'. */
  files?: RelativeFile[];
  /** Callback to update files. For 'single', will be called with [file] or [] */
  setFiles: (_files: RelativeFile[], _nameOrDir?: string) => void;
  height?: string | number;
  variant?: 'compact' | 'portal';
};

/** ---------- helpers ---------- */

const splitPath = (p: string): string[] => p.split(/[\\/]+/).filter(Boolean);
const bestPath = (f: FileWithPath) => f.path || (f as any).webkitRelativePath || f.name;

function relativizeBatch(files: FileWithPath[]) {
  if (files.length === 0) return [];

  const segments = files.map((f) => splitPath(bestPath(f)));
  const dirSegs = segments.map((s) => s.slice(0, Math.max(0, s.length - 1)));

  let commonLen = 0;

  if (files.length === 1) {
    commonLen = dirSegs[0]?.length > 0 ? 1 : 0;
  } else if (dirSegs.every((d) => d.length > 0)) {
    const minLen = Math.min(...dirSegs.map((d) => d.length));
    for (let i = 0; i < minLen; i++) {
      const seg = dirSegs[0][i];
      if (dirSegs.every((d) => d[i] === seg)) commonLen++;
      else break;
    }
  }

  return files.map((f, i) => {
    const s = segments[i];
    const filename = s[s.length - 1] ?? f.name;
    const rel = s.slice(commonLen);
    const path = rel.length ? rel.join('/') : filename;
    return { file: f, path } as RelativeFile;
  });
}

export default function CustomDropZone({
  mode,
  files = [],
  setFiles,
  height = '20px',
  variant = 'compact',
}: Props) {
  const directoryInputRef = useRef<HTMLInputElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Ensure folder attribute is actually present on the DOM node
  useEffect(() => {
    if (!directoryInputRef.current) return;
    directoryInputRef.current.setAttribute('webkitdirectory', '');
    directoryInputRef.current.setAttribute('directory', '');
    directoryInputRef.current.setAttribute('mozdirectory', '');
  }, []);

  const resetHiddenInputs = () => {
    // Clearing .value (NOT remounting) is the safest way to allow re-selecting the same file/folder
    if (directoryInputRef.current) directoryInputRef.current.value = '';
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  useEffect(() => {
    if (files.length === 0) resetHiddenInputs();
  }, [files.length]);

  const onReject = (badFiles: FileRejection[]) => {
    const fileStr = badFiles
      .map((rejection) => (rejection.file as any).path || rejection.file.name)
      .join(', ');
    notifications.show({
      title: 'Files too large - Max file size is 1GB',
      message: 'Rejected files: ' + fileStr,
      color: 'red',
    });
  };

  const applyFilesFromDropOrPicker = (acceptedFiles: FileWithPath[]) => {
    if (mode === 'single') {
      // Disallow zips for single-file uploads
      const zipFiles = acceptedFiles.filter((f) => f.name.toLowerCase().endsWith('.zip'));
      if (zipFiles.length > 0) {
        notifications.show({
          title: 'ZIP files not allowed for single file upload',
          message: 'You can only upload ZIP files for folder inputs.',
          color: 'red',
        });
        acceptedFiles = acceptedFiles.filter((f) => !f.name.toLowerCase().endsWith('.zip'));
        if (acceptedFiles.length === 0) return;
      }

      const f = acceptedFiles[0];
      if (!f) {
        setFiles([]);
        return;
      }
      const path = (f as any).path || (f as any).webkitRelativePath || f.name;
      setFiles([{ file: f, path }]);
      return;
    }

    // multiple mode
    const normalizedNew = relativizeBatch(acceptedFiles);
    const singleFileName =
      files.length === 0 && normalizedNew.length === 1 ? normalizedNew[0].file.name : undefined;

    setFiles([...files, ...normalizedNew], singleFileName);
  };

  const handleDrop = (acceptedFiles: FileWithPath[]) => {
    applyFilesFromDropOrPicker(acceptedFiles);
    resetHiddenInputs();
  };

  const onDirectoryChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    if (mode !== 'multiple') return;

    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;

    const newFiles = Array.from(fileList) as any[];

    const first = newFiles[0];
    const relPath: string = first.webkitRelativePath || first.path || first.name;
    const directoryName = relPath.split('/')[0] || 'folder';

    const mapped: RelativeFile[] = newFiles.map((f) => {
      const p: string = f.webkitRelativePath || f.path || f.name;
      const trimmed = p.startsWith(directoryName + '/') ? p.slice(directoryName.length + 1) : p;
      return { file: f, path: trimmed };
    });

    setFiles([...files, ...mapped], directoryName);
    resetHiddenInputs();
  };

  const onFilePickerChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;

    const picked = Array.from(fileList) as FileWithPath[];
    applyFilesFromDropOrPicker(picked);
    resetHiddenInputs();
  };

  const openFilePicker = () => {
    // Clear first so selecting the same file again still triggers change
    if (fileInputRef.current) fileInputRef.current.value = '';
    fileInputRef.current?.click();
  };

  const openFolderPicker = () => {
    if (mode !== 'multiple') return;
    // Clear first so selecting the same folder again still triggers change
    if (directoryInputRef.current) directoryInputRef.current.value = '';
    directoryInputRef.current?.click();
  };

  const isPortalVariant = variant === 'portal';
  const uploadLabel = mode === 'single' ? 'file' : 'files';
  const selectedPortalSingleFile =
    isPortalVariant && mode === 'single' && files.length > 0 ? files[0] : undefined;

  return (
    <>
      <Dropzone
        multiple={mode === 'multiple'}
        onDrop={handleDrop}
        onReject={onReject}
        maxSize={1024 * 1024 * 1024}
        activateOnClick={false}
        activateOnKeyboard={false}
        // Click on the zone should open the normal file picker
        onMouseDown={(e) => {
          if (e.button !== 0) return; // left click only
          openFilePicker();
        }}
        style={{
          background: '#FFFFFF',
          border: isPortalVariant ? '1px dashed #CBD5E1' : undefined,
          borderRadius: isPortalVariant ? 10 : undefined,
          cursor: 'pointer',
        }}
      >
        <Stack
          justify="center"
          // keep your original behavior: all clicks go to Dropzone root (except the Anchor)
          style={{ pointerEvents: 'none', height }}
          align="center"
          gap={isPortalVariant ? 'xs' : '4px'}
        >
          {selectedPortalSingleFile ? (
            <>
              <div
                style={{
                  alignItems: 'center',
                  background: '#F1F5F9',
                  borderRadius: '50%',
                  display: 'flex',
                  flex: '0 0 48px',
                  flexShrink: 0,
                  height: 48,
                  justifyContent: 'center',
                  minHeight: 48,
                  minWidth: 48,
                  width: 48,
                }}
              >
                <HiOutlineDocumentText size={22} color="#475569" />
              </div>
              <Stack gap={2} align="center" style={{ minWidth: 0, maxWidth: '100%' }}>
                <Tooltip label={selectedPortalSingleFile.file.name} openDelay={250}>
                  <Text size="sm" fw={700} c="#111827" truncate style={{ maxWidth: '100%' }}>
                    {selectedPortalSingleFile.file.name}
                  </Text>
                </Tooltip>
                <Text size="sm" c="#64748B">
                  {formatBytes(selectedPortalSingleFile.file.size, 1)}
                </Text>
              </Stack>
              <Button
                variant="default"
                size="sm"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setFiles([]);
                }}
                style={{ flex: '0 0 auto', height: 32, minHeight: 32, pointerEvents: 'all' }}
              >
                Remove file
              </Button>
            </>
          ) : isPortalVariant ? (
            <>
              <div
                style={{
                  alignItems: 'center',
                  background: '#F1F5F9',
                  borderRadius: '50%',
                  display: 'flex',
                  flex: '0 0 48px',
                  flexShrink: 0,
                  height: 48,
                  justifyContent: 'center',
                  minHeight: 48,
                  minWidth: 48,
                  width: 48,
                }}
              >
                <Dropzone.Accept>
                  <HiOutlineArrowUpTray size={22} color="var(--mantine-color-blue-6)" />
                </Dropzone.Accept>
                <Dropzone.Reject>
                  <HiOutlineExclamationTriangle size={22} color="var(--mantine-color-red-6)" />
                </Dropzone.Reject>
                <Dropzone.Idle>
                  <HiOutlineArrowUpTray size={22} color="#475569" />
                </Dropzone.Idle>
              </div>
              <Stack gap={2} align="center">
                <Text size="sm" fw={700} c="#111827">
                  Drag and drop your {uploadLabel} here
                </Text>
                <Text size="sm" c="#64748B">
                  Select {mode === 'single' ? 'one file' : 'files'} to upload
                </Text>
              </Stack>
              <Button
                variant="default"
                size="sm"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  openFilePicker();
                }}
                style={{ flex: '0 0 auto', height: 32, minHeight: 32, pointerEvents: 'all' }}
              >
                Browse files
              </Button>
              {mode === 'multiple' && (
                <Group gap={4} style={{ pointerEvents: 'all' }}>
                  <Text size="xs" c="dimmed">
                    Upload a folder
                  </Text>
                  <Anchor
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      openFolderPicker();
                    }}
                    c="brandBlue.6"
                    size="xs"
                    style={{ cursor: 'pointer' }}
                  >
                    here
                  </Anchor>
                </Group>
              )}
            </>
          ) : (
            <Group gap="4px" align="center" justify="center">
              <Dropzone.Accept>
                <HiOutlineArrowUpTray size={14} color="var(--mantine-color-blue-6)" />
              </Dropzone.Accept>
              <Dropzone.Reject>
                <HiOutlineExclamationTriangle size={14} color="var(--mantine-color-red-6)" />
              </Dropzone.Reject>
              <Dropzone.Idle>
                <HiOutlineArrowUpTray size={14} color="var(--mantine-color-dimmed)" />
              </Dropzone.Idle>

              <Text size="xs" c="dimmed" ml="xs">
                {mode === 'single' ? 'Upload a file' : 'Upload files'}
              </Text>

              {mode === 'multiple' && (
                <>
                  <Text size="xs" c="dimmed">
                    or click to upload a folder
                  </Text>
                  <Anchor
                    // stop the Dropzone mouseDown from opening the file picker
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      openFolderPicker();
                    }}
                    c="brandBlue.6"
                    size="xs"
                    style={{ pointerEvents: 'all', cursor: 'pointer' }}
                  >
                    here
                  </Anchor>
                </>
              )}
            </Group>
          )}
        </Stack>
      </Dropzone>

      {/* Hidden: normal file picker (used for clicking the dropzone) */}
      <input
        ref={fileInputRef}
        type="file"
        multiple={mode === 'multiple'}
        style={{ display: 'none' }}
        onChange={onFilePickerChange}
      />

      {/* Hidden: directory picker (multiple only) */}
      {mode === 'multiple' && (
        <input
          ref={directoryInputRef}
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={onDirectoryChange}
        />
      )}
    </>
  );
}
